import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Grid,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import MaterialReactTable from "material-react-table";
import { useEffect, useState } from "react";
import { API_SLUGS, VARIABLE_TYPES } from "../../constants";
import { Plus } from "../../icons/plus";
import { axiosInstance } from "../../services/axios";
import { IAlertState, useAlertState } from "../../store/alertState";
import CommonPopper from "../common/commonPopper";
import DefaultVariable from './defaultVariableForm';
import OneOfJsonVariable from './oneOfJsonVariableForm';
import NumberVariable from './numberVariableForm';
import VariableName from './varaibleNameForm';
import copy from 'copy-to-clipboard';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IosShareIcon from '@mui/icons-material/IosShare';
import ModalWrapper from '../common/modal/wrapper';
import AddVariableForm from './addVariableForm';
import { BorderColorOutlined } from '@mui/icons-material';
import DataObjectIcon from '@mui/icons-material/DataObject';
import UpdateVariableForm from './updateVariableForm';
import JsonList from './jsonListForm';

interface IVariableData {
    selectedVariable: string,
    isReadOnly?: boolean,
    isGlobalPattern?: boolean,
    onDataChange?: (hasChanges: boolean) => void;
}

export const validateVariableDataValues = (value: any, valueDataType: string, errorMessage: string,) => {
    if (!value || value === "") {
        return (errorMessage)
    }
    else return ""
}

const VariableData: React.FC<IVariableData> = ({
    selectedVariable,
    isReadOnly,
    isGlobalPattern,
    onDataChange
}) => {
    const [loading, setLoading] = useState(false);
    const [variableResponseData, setVariableResponseData] = useState<any>({});
    const { setMessage } = useAlertState() as IAlertState;
    const [tableData, setTableData] = useState<any[]>([]);
    const [isDataChanged, setIsDataChanged] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [loadJsonOpen, setLoadJsonOpen] = useState(false);
    const [exportJsonOpen, setExportJsonOpen] = useState(false);
    const [jsonInput, setJsonInput] = useState("");
    const [jsonError, setJsonError] = useState("");
    const [validationErrors, setValidationErrors] = useState<{ [index: number]: boolean }>({});

    useEffect(() => {
        setInitialData(JSON.parse(JSON.stringify(tableData)));
    }, []);

    useEffect(() => {
        fetchVariableData(true)
    }, [])

    useEffect(() => {
        onDataChange?.(isDataChanged);
    }, [isDataChanged, tableData]);

    const fetchVariableData = async (initialize = false) => {

        try {
            setLoading(true);
            const path = isGlobalPattern ? API_SLUGS.GET_VARIABLES_DATA.replace('{id}', selectedVariable) : API_SLUGS.PATTERN.replace('{id}', selectedVariable);
            let variableData = await axiosInstance.get(path)
            setVariableResponseData(variableData?.data)
            setTableData(variableData?.data?.data?.variables);
            const fetchedData = variableData?.data?.data?.variables || [];

            if (initialize) {
                setInitialData(JSON.parse(JSON.stringify(fetchedData)));
            }
            setLoading(false);
        } catch (error: any) {
            setMessage({ title: error.response?.data?.message, type: "error" });
            setLoading(false)
        }

    }

    const handleChange = async (value: any, valueType: "name" | "type" | "value", index: number) => {
        let newValues = [...tableData];
        if (valueType === "type") {
            newValues[index].value = null;
        } 
        newValues[index][valueType] = value;
        setIsDataChanged(true)
        setTableData(newValues);
    };

    const handleAddRow = () => {
        const newRow: any = {
            name: "",
            type: "StringConstant",
            value: "",
            deleted: false,
        };
        setTableData([newRow, ...tableData]);
    };

    const deleteVariableData = (index: number) => {
        const newValue = [...tableData];
        newValue[index].deleted = true;
        setTableData(newValue);
        setIsDataChanged(true);
    }

    const handleSave = async () => {
        if (!validateTableDataGlobally()) return;
        try {
            const updatedObject = {
                data: {
                    variables: trimTableData().map(({ name, type, value }) =>
                        ({ name, type, value })
                    )
                }
            }
            const path = isGlobalPattern ? API_SLUGS.GET_VARIABLES_DATA.replace('{id}', selectedVariable) : API_SLUGS.PATTERN.replace('{id}', selectedVariable);
            const response = await axiosInstance.patch(path, updatedObject);
            setMessage({ title: "Pattern updated successfully", type: "success" });
            setIsDataChanged(false)
            fetchVariableData(true);
        } catch (error: any) {
            setMessage({ title: error.response?.data?.message, type: "error" });
        }
    }
    const trimTableData = () => {
        const trimmedTableData = [...tableData];
        let newIndex = 0;
        tableData.forEach((data: any, index: number) => {
            if ((data.name === "" && data.type === "" && data.value === "") || data.deleted) {
                trimmedTableData.splice(newIndex, 1);
                newIndex--;
            }
            newIndex++
        })
        return trimmedTableData;
    }


    const validateTableDataGlobally = () => {
        let isValid = true;
        const tableDataToValidate = tableData?.filter((row) => !row?.deleted)
        for (let i = 0; i < tableDataToValidate?.length; i++) {
            const row = tableDataToValidate?.[i];
            if (!row?.name || !row?.type || !row?.value) {
                isValid = false;
                break;
            }
        }
        if (!isValid) {
            setMessage({
                title: "Please fill out all fields (name, type, value) before saving.",
                type: "error"
            });
        }
        return isValid;
    };

    const validateJsonSchema = (data: any[]): string | null => {
        if (!Array.isArray(data)) return "JSON should be an array of objects.";

        const nameSet = new Set();

        for (let i = 0; i < data.length; i++) {
            const item = data[i];

            if (typeof item !== "object" || Array.isArray(item)) {
                return `Item at index ${i} is not a valid object.`;
            }

            if (typeof item.name !== "string" || item.name.trim() === "") {
                return `Item at index ${i} must have a non-empty 'name' field of type string.`;
            }

            const trimmedName = item.name.trim();

            if (nameSet.has(trimmedName)) {
                return `Duplicate variable name '${trimmedName}' found at index ${i}. Each variable name must be unique.`;
            }
            nameSet.add(trimmedName);

            const isValidType = VARIABLE_TYPES.find((type) => type.key === item.type);
            if (!isValidType) {
                return `Item at index ${i} has an invalid 'type'.`;
            }

            if (!("value" in item)) {
                return `Item at index ${i} must have a 'value' field.`;
            }
        }

        return null;
    };


    const handleLoadJson = () => {
        try {
            const parsed = JSON.parse(jsonInput);
            const schemaError = validateJsonSchema(parsed);
            if (schemaError) throw new Error(schemaError);
            setTableData(parsed);
            setLoadJsonOpen(false);
            setJsonError("");
            setIsDataChanged(true)
        } catch (e: any) {
            setJsonError(e.message);
        }
    };

    const handleExportJson = () => {
        return JSON.stringify(tableData, null, 2);
    };

    const handleJsonUpload = () => {
        const tableDataToShow = tableData.map((row) => {
            delete row.deleted
            return row
        })
        setJsonInput(JSON.stringify(tableDataToShow, null, 2));
        setLoadJsonOpen(true)
    }

    const handleCopyJson = () => {
        copy(handleExportJson());
        setMessage({ title: "Json copied to clipboard", type: "success" })
    }


    return (
        <Box >
            <Box display={'flex'} alignItems={'center'}>
                <Typography variant='h5' sx={{ ml: 1 }}> {variableResponseData?.name}</Typography> 
                {!isReadOnly && (<ModalWrapper
                    maxWidth="sm"
                    title="Edit Pattern Name"
                    trigger={
                        <Tooltip title="Edit Name">
                            <Button variant='outlined' size='small' sx={{ color: 'text.primary', borderColor: 'neutral.300', mr: 1, minWidth: '45px', ml: 0.5 }}>
                                <BorderColorOutlined fontSize="small" />
                            </Button>
                        </Tooltip>

                    }
                    child={
                        <UpdateVariableForm
                            callback={() => fetchVariableData(true)}
                            isGlobalPattern={isGlobalPattern}
                            defaultFormValues={variableResponseData}
                            updateUUID={selectedVariable}

                        />
                    }
                />)}
            </Box>
            <Box>
                <Grid container sx={{ mt: 2 }} columnSpacing={4} >
                    <Grid item xs={12}>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: "space-between" }}>
                                <Box>
                                    {!isReadOnly && <Button size="small" variant="outlined" sx={{ my: 2, ml: 1, borderColor: 'neutral.300', color: 'text.primary' }} onClick={handleAddRow}>
                                        Add Pattern <Plus />
                                    </Button>}
                                    <Button variant="outlined" size="small" sx={{ mx: 2, borderColor: 'neutral.300', color: 'text.primary' }} onClick={() => handleJsonUpload()}>
                                        JSON View<DataObjectIcon sx={{ ml: 1 }} fontSize='small' />
                                    </Button>

                                </Box>
                                {!isReadOnly && <Button
                                    color="primary"
                                    size="small"
                                    variant="contained"
                                    onClick={handleSave}
                                    sx={{ my: 2, mr: 2, py: 0.5 }}
                                    disabled={!isDataChanged || Object.values(validationErrors).some((err) => err)}
                                >
                                    Save
                                </Button>}
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: "flex" }}>


                            </Box>
                        </Grid>

                        <MaterialReactTable
                            muiTablePaperProps={{
                                elevation: 0,
                                sx: { marginBottom: 0 }
                            }}
                            enableGlobalFilter={false}
                            columns={[
                                {
                                    accessorKey: "name",
                                    header: "Field Pattern",
                                    Cell: (params: any) => {
                                        return (
                                            <VariableName
                                                data={tableData}
                                                index={params?.row?.index}
                                                onChange={handleChange}
                                                setValidationError={(hasError: boolean) =>
                                                    setValidationErrors((prev) => ({ ...prev, [params?.row?.index]: hasError }))
                                                  }
                                            />
                                        )
                                    },
                                    size: 130,
                                },
                                {
                                    accessorKey: "type",
                                    header: "Type",
                                    Cell: (params: any) => (
                                        <>
                                            <Select
                                                value={tableData?.[params?.row?.index]?.type}
                                                onChange={(e) => handleChange(e.target.value, "type", params?.row?.index)}
                                                sx={{ minWidth: "10em", maxWidth: "100%", textAlign: "left", flex: 1 }}
                                            >
                                                {VARIABLE_TYPES.map((type) => (
                                                    <MenuItem key={type.key} value={type.key}>
                                                        {type.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText error={true}>{""}</FormHelperText>
                                        </>
                                    )
                                },
                                {
                                    accessorKey: "value",
                                    header: "Value Pattern",
                                    Cell: (params: any) => {
                                        
                                        if (tableData?.[params?.row?.index]?.type === 'json') {
                                            const jsonLength = tableData?.[params?.row?.index]?.value?.length;
                                            return (
                                                <ModalWrapper
                                                maxWidth="sm"
                                                title={""}
                                                trigger={
                                                   <Tooltip title={tableData?.[params?.row?.index]?.value || "No JSON"}> 
                                                    <Button variant="outlined" sx={{ borderColor: 'neutral.300', color: 'text.primary'}} size="small">{jsonLength > 0 ? jsonLength + " JSON" : "Add JSON"}
                                                        </Button>  
                                                    </Tooltip>
                                                }
                                                child={<JsonList  data={tableData}
                                                index={params?.row?.index}
                                                onChange={handleChange}
                                                callback={() => {}}
                                                  />
                                                }
                                                 
                                              />
                                           
                                            )
                                        } else if (tableData?.[params?.row?.index]?.type === 'IntConstant') {
                                            return (
                                                <NumberVariable
                                                    data={tableData}
                                                    index={params?.row?.index}
                                                    onChange={handleChange}
                                                    setValidationError={(hasError: boolean) =>
                                                        setValidationErrors((prev) => ({ ...prev, [params?.row?.index]: hasError }))
                                                      }
                                                />
                                            )
                                        } else {
                                            return (
                                                <DefaultVariable
                                                    data={tableData}
                                                    index={params?.row?.index}
                                                    onChange={handleChange}
                                                    setValidationError={(hasError: boolean) =>
                                                        setValidationErrors((prev) => ({ ...prev, [params?.row?.index]: hasError }))
                                                      }
                                                />
                                            )
                                        }
                                    },
                                },
                                {
                                    //@ts-ignore
                                    accessorKey: "actions",
                                    header: "Actions",
                                    Cell: (params: any) => {
                                        return (

                                           !isReadOnly && (<Box sx={{ display: "flex", justifyContent: "start", alignContent: "top" }}>
                                                <Tooltip title="Delete">
                                                    <IconButton color="error" onClick={() => deleteVariableData(params?.row?.index)} disabled={params?.row?.original?.isDeleted}>
                                                        <DeleteOutlineIcon  />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>)

                                        )
                                    },
                                },
                            ]}
                            data={tableData}
                            initialState={{
                                density: "compact",
                            }}
                            state={{
                                showProgressBars: loading,
                            }}
                            enableColumnActions={false}
                            enableSorting={false}
                            enableStickyHeader
                            muiTableBodyCellProps={{
                                align: 'justify',
                            }}
                            muiTableBodyRowProps={({ row }) => {
                                const isDeleted = tableData[row.index]?.deleted;
                                return {
                                    sx: {
                                        backgroundColor: isDeleted ? '#ffebee' : 'inherit',
                                        opacity: isDeleted ? 0.6 : 1,
                                    },
                                };
                            }}
                            muiTableContainerProps={{
                                sx: { maxHeight: '60vh', overflow: 'auto' },
                            }}
                            enableTopToolbar={false}
                            enablePagination={false}
                            enableDensityToggle={false}
                            enableColumnFilters={false}
                        />


                    </Grid>
                </Grid>


                <Dialog open={loadJsonOpen} onClose={() => setLoadJsonOpen(false)} fullWidth maxWidth="md">
                    <DialogTitle display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant='h5'>JSON View</Typography>
                        <Tooltip className="hand" title='Copy Json Content'><Button onClick={handleCopyJson}>Copy<ContentCopyIcon sx={{ ml: 1 }} fontSize='small' /></Button></Tooltip>
                    </DialogTitle>
                    <DialogContent>
                    <TextField
                            multiline
                            fullWidth
                            minRows={10}
                            value={jsonInput}
                            onChange={(e) => setJsonInput(e.target.value)}
                            placeholder={`[\n  {\n    "name": "dummy",\n    "type": "StringConstant",\n    "value": "test"\n  }\n]`}
                            InputProps={{
                                sx: {
                                    fontFamily: '"Fira Code", "Fira Mono", monospace',
                                    fontSize: 12,
                                }
                            }}
                        />

                    </DialogContent>
                    <DialogActions sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ pl: 2 }}>
                            {jsonError && (
                                <Alert sx={{ mb: 1 }} color="error" severity="error">
                                    {jsonError}
                                </Alert>
                            )}
                        </Box>
                        <Box sx={{ mr: 4 }}>
                            <Button onClick={() => setLoadJsonOpen(false)}>Cancel</Button>
                           {!isReadOnly && <Button variant="contained" onClick={handleLoadJson}>Save</Button>}
                        </Box>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};


export default VariableData;  